import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, Search, X, FileSpreadsheet, FileText } from 'lucide-react';
import type { Account, SiteOption } from '../types';

interface LocationsFiltersBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  accountId: string;
  onAccountChange: (value: string) => void;
  siteId: string;
  onSiteChange: (value: string) => void;
  pageSize: number;
  onPageSizeChange: (value: number) => void;
  accounts: Account[];
  sites: SiteOption[];
  isLoadingAccounts: boolean;
  isLoadingSites: boolean;
  onExport?: () => void;
  onExportExcel?: () => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

export function LocationsFiltersBar({
  search,
  onSearchChange,
  accountId,
  onAccountChange,
  siteId,
  onSiteChange,
  pageSize,
  onPageSizeChange,
  accounts,
  sites,
  isLoadingAccounts,
  isLoadingSites,
  onExport,
  onExportExcel,
  hasActiveFilters = false,
  onClearFilters,
}: LocationsFiltersBarProps) {
  const { t } = useTranslation('locations');

  return (
    <div className="flex flex-wrap items-center gap-3 flex-1">
      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-[300px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('filters.searchPlaceholder', 'Search locations...')}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Account Filter */}
      <Select value={accountId} onValueChange={onAccountChange}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder={t('filters.allAccounts', 'All Accounts')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.allAccounts', 'All Accounts')}</SelectItem>
          {!isLoadingAccounts &&
            accounts.map((account) => (
              <SelectItem key={account.id} value={account.id}>
                {account.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      {/* Site Filter */}
      <Select value={siteId} onValueChange={onSiteChange}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder={t('filters.allSites', 'All Sites')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filters.allSites', 'All Sites')}</SelectItem>
          {!isLoadingSites &&
            sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>

      {/* Page Size */}
      <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
        <SelectTrigger className="w-[100px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="10">10</SelectItem>
          <SelectItem value="25">25</SelectItem>
          <SelectItem value="50">50</SelectItem>
          <SelectItem value="100">100</SelectItem>
        </SelectContent>
      </Select>

      {/* Export */}
      {onExport && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-1" />
              {t('toolbar.export', 'Export')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExport}>
              <FileText className="h-4 w-4 mr-2" />
              {t('toolbar.exportCSV', 'Export CSV')}
            </DropdownMenuItem>
            {onExportExcel && (
              <DropdownMenuItem onClick={onExportExcel}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t('toolbar.exportExcel', 'Export Excel')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Clear Filters */}
      {hasActiveFilters && onClearFilters && (
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-1" />
          {t('filters.clearAll', 'Clear')}
        </Button>
      )}
    </div>
  );
}
